import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { AlignCenterIcon } from '../../assets/icons/AlignCenterIcon'
import { AlignJustifyIcon } from '../../assets/icons/AlignJustifyIcon'
import { AlignLeftIcon } from '../../assets/icons/AlignLeftIcon'
import { AlignRightIcon } from '../../assets/icons/AlignRightIcon'
import { BoldIcon } from '../../assets/icons/BoldIcon'
import { FontColorIcon } from '../../assets/icons/FontColorIcon'
import { FontIcon } from '../../assets/icons/FontIcon'
import { ItalicIcon } from '../../assets/icons/ItalicIcon'
import { LinkIcon } from '../../assets/icons/LinkIcon'
import { RedoIcon } from '../../assets/icons/RedoIcon'
import { UnderlineIcon } from '../../assets/icons/UnderlineIcon'
import { UndoIcon } from '../../assets/icons/UndoIcon'
import { getMergedClassNames, getUniqueKey } from '../../utils/HTMLutils'
import DropdownMenu from '../dropdownMenu/DropdownMenu'
import TextEditorColorPicker from './components/textEditorColorPicker/TextEditorColorPicker'
import TextEditorHeaderButton from './components/textEditorHeaderButton/TextEditorHeaderButton'
import TextEditorTitleChoser from './components/textEditorTitleChoser/TextEditorTitleChoser'
import TextEditorUrlCreator from './components/textEditorUrlCreator/TextEditorUrlCreator'
import './TextEditor.scss'
const rootClassName = 'component-text-editor'

export default function TextEditor(props) {
  const [id] = useState(getUniqueKey())
  const [fontOpened, setFontOpened] = useState(false)
  const [fontColorOpened, setFontColorOpened] = useState(false)
  const [linkOpened, setLinkOpened] = useState(false)
  const [cursor, setCursor] = useState({})

  const getProps = () => {
    const p = {
      ...props,
      className: getMergedClassNames([
        rootClassName,
        props.className || '',
        props.disabled ? 'disabled' : '',
        props.error ? 'error' : ''
      ])
    }
    delete p.topLabel
    delete p.helperText
    delete p.hideTopLabel
    delete p.hideHelperText
    delete p.error
    return p
  }

  const format = (command, value, toggle = false) => {
    document.execCommand(command, toggle, value)
    verifyCursor()
  }

  const verifyCursor = () => {
    let isLink = false
    let fore = ''
    const selection = window.getSelection()
    if (!selection || !selection.anchorNode) return
    let aux = selection.anchorNode.parentElement
    // let fontCount = 0
    while (!aux.hasAttribute('class', `${rootClassName}-editor`)) {
      if (aux.tagName === 'A') {
        isLink = true
      } else if (aux.tagName === 'FONT') {
        fore = aux.getAttribute('color')
      }
      aux = aux.parentElement
    }

    setCursor({
      ...cursor,
      link: isLink,
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      foreColor: fore,
      justifyLeft: document.queryCommandState('justifyLeft'),
      justifyRight: document.queryCommandState('justifyRight'),
      justifyCenter: document.queryCommandState('justifyCenter'),
      justifyFull: document.queryCommandState('justifyFull')
    })
  }

  const handleChange = (evt) => {
    const editor = document.querySelector(`#${id}`)
    if (typeof props.onChange === 'function')
      props.onChange(
        editor.innerHTML,
        String(editor.innerText).trim().replace(/\n/g, ' ')
      )
  }

  const handlePaste = (evt) => {
    evt.preventDefault()
    const text = evt.clipboardData
      ? (evt.originalEvent || evt).clipboardData.getData('text/plain')
      : // For IE
      window.clipboardData
      ? window.clipboardData.getData('Text')
      : ''
    if (document.queryCommandSupported('insertText')) {
      document.execCommand('insertText', false, text)
    } else {
      const range = document.getSelection().getRangeAt(0)
      range.deleteContents()

      const textNode = document.createTextNode(text)
      range.insertNode(textNode)
      range.selectNodeContents(textNode)
      range.collapse(false)

      const selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  useEffect(() => {
    const editor = document.querySelector(`#${id}`)
    editor.addEventListener('input', handleChange)
    editor.addEventListener('paste', handlePaste)
    return () => {
      editor.remove('paste', handlePaste)
      editor.removeEventListener('input', handleChange)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Set Value
  useEffect(() => {
    const editor = document.querySelector(`#${id}`)
    if (editor.innerHTML.toString() !== props.value) {
      editor.innerHTML = props.value
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.value])

  return (
    <div className={`${rootClassName}-outer`}>
      {!props.hideTopLabel && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-top-label`,
            props.disabled ? 'disabled' : '',
            props.error ? 'error' : ''
          ])}
        >
          {props.topLabel}
        </div>
      )}
      <div {...getProps()}>
        <div className={`${rootClassName}-header`}>
          <div className={`${rootClassName}-left-content`}>
            <DropdownMenu
              opened={fontOpened}
              setOpened={setFontOpened}
              content={
                <TextEditorTitleChoser
                  opened={fontOpened}
                  onChange={(title = 'H6') => {
                    document.execCommand('formatBlock', false, title)
                    setFontOpened(false)
                  }}
                />
              }
            >
              <TextEditorHeaderButton
                disabled={props.disabled}
                onClick={() => {
                  document.querySelector(`#${id}`).focus()
                  setFontOpened(!fontOpened)
                }}
                icon={<FontIcon />}
              />
            </DropdownMenu>
            <DropdownMenu
              opened={fontColorOpened}
              setOpened={setFontColorOpened}
              content={
                <TextEditorColorPicker
                  opened={fontColorOpened}
                  atualColor={cursor.foreColor}
                  onChange={(color) => {
                    format('foreColor', color)
                    setFontColorOpened(false)
                    document.querySelector(`#${id}`).focus()
                  }}
                />
              }
            >
              <TextEditorHeaderButton
                disabled={props.disabled}
                onClick={() => {
                  document.querySelector(`#${id}`).focus()
                  setFontColorOpened(!fontColorOpened)
                }}
                icon={<FontColorIcon gota={cursor.foreColor} />}
              />
            </DropdownMenu>
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.bold}
              icon={<BoldIcon />}
              onClick={() => {
                format('bold')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.italic}
              icon={<ItalicIcon />}
              onClick={() => {
                format('italic')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.underline}
              icon={<UnderlineIcon />}
              onClick={() => {
                format('underline')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <DropdownMenu
              opened={linkOpened}
              setOpened={setLinkOpened}
              content={
                <TextEditorUrlCreator
                  editorId={id}
                  opened={linkOpened}
                  onChange={(text, link, newTab) => {
                    format(
                      'insertHTML',
                      `<a href="${link}"${
                        newTab ? 'target="_blank"' : ''
                      }>${text}</a>`
                    )
                    setLinkOpened(false)
                    document.querySelector(`#${id}`).focus()
                  }}
                />
              }
            >
              <TextEditorHeaderButton
                disabled={props.disabled}
                active={cursor.link}
                onClick={() => {
                  document.querySelector(`#${id}`).focus()
                  setLinkOpened(!linkOpened)
                }}
                icon={<LinkIcon />}
              />
            </DropdownMenu>
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.justifyLeft}
              icon={<AlignLeftIcon />}
              onClick={() => {
                format('justifyLeft')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.justifyFull}
              icon={<AlignJustifyIcon />}
              onClick={() => {
                format('justifyFull')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.justifyCenter}
              icon={<AlignCenterIcon />}
              onClick={() => {
                format('justifyCenter')
                document.querySelector(`#${id}`).focus()
              }}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              active={cursor.justifyRight}
              icon={<AlignRightIcon />}
              onClick={() => {
                format('justifyRight')
                document.querySelector(`#${id}`).focus()
              }}
            />
          </div>
          <div className={`${rootClassName}-right-content`}>
            <TextEditorHeaderButton
              disabled={props.disabled}
              icon={<UndoIcon />}
              onClick={() => format('undo')}
            />
            <TextEditorHeaderButton
              disabled={props.disabled}
              icon={<RedoIcon />}
              onClick={() => format('redo')}
            />
          </div>
        </div>
        <div
          id={id}
          className={`${rootClassName}-editor`}
          contentEditable={!props.disabled}
          onClick={verifyCursor}
          onKeyUp={verifyCursor}
        />
      </div>
      {!props.hideHelperText && (
        <div
          className={getMergedClassNames([
            `${rootClassName}-helper-text`,
            props.disabled ? 'disabled' : '',
            props.error ? 'error' : ''
          ])}
        >
          {props.helperText}
        </div>
      )}
    </div>
  )
}

TextEditor.propTypes = {
  disabled: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  topLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  hideTopLabel: PropTypes.bool,
  hideHelperText: PropTypes.bool,
  error: PropTypes.bool
}

TextEditor.defaultProps = {
  disabled: false,
  value: '',
  onChange: () => {}
}
